import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ContractInsightsData } from '@/pages/ContractDetail';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ListPagination } from '@/components/ListPagination';
import { Badge } from '@/components/ui/badge';
import { Search } from 'lucide-react';
import { format } from 'date-fns';
import { useDebounce } from '@/hooks/useDebounce';
import { cn } from '@/lib/utils';

interface Props {
    data: ContractInsightsData;
}

const PAGE_SIZE = 20;

interface TransactionRecord {
    id: number;
    amount: number;
    transaction_at: string;
    description: string | null;
    transaction_type: string;
    note: string | null;
    total_count: number;
}



const getTypeBadge = (type: string) => {
    switch (type) {
        case 'deposit':
            return <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">Deposit</Badge>;
        case 'deposit_refund':
            return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Deposit Refund</Badge>;
        case 'loan_repayment':
            return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Advance</Badge>;
        case 'insurance_loan_repayment':
            return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Ins. Advance</Badge>;
        case 'insurance_loan_deposit':
            return <Badge variant="outline" className="bg-pink-50 text-pink-700 border-pink-200">Ins. Deposit</Badge>;
        default:
            return null; // repayment - no badge needed, it's the default
    }
};

export default function ContractInsightPayments({ data }: Props) {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 300);

    const contractId = data.contract?.id;

    // Reset to page 1 when search changes
    useEffect(() => { setCurrentPage(1); }, [debouncedSearch]);

    // Fetch transactions using new RPC
    const { data: transactionsData, isLoading } = useQuery({
        queryKey: ['contract-transactions', contractId, currentPage, debouncedSearch],
        queryFn: async () => {
            if (!contractId) return { transactions: [], total: 0 };

            const { data, error } = await supabase.rpc('get_contract_transactions', {
                p_contract_id: contractId,
                p_search: debouncedSearch || null,
                p_page: currentPage,
                p_page_size: PAGE_SIZE,
            });

            if (error) throw error;
            const records = (data || []) as TransactionRecord[];
            return {
                transactions: records,
                total: records[0]?.total_count || 0
            };
        },
        enabled: !!contractId,
    });

    // Fetch all transactions for stats (without pagination)
    const { data: allTransactions } = useQuery({
        queryKey: ['contract-transactions-all', contractId],
        queryFn: async () => {
            if (!contractId) return [];

            const { data, error } = await supabase.rpc('get_contract_transactions', {
                p_contract_id: contractId,
                p_search: null,
                p_page: 1,
                p_page_size: 10000, // Get all for stats
            });

            if (error) throw error;
            return (data || []) as TransactionRecord[];
        },
        enabled: !!contractId,
    });

    // Compute stats
    const stats = useMemo(() => {
        if (!allTransactions || allTransactions.length === 0) {
            return {
                totalPayments: 0,
                paymentCount: 0,
                averagePayment: 0,
                lastPayment: null as Date | null,
                largestPayment: 0,
            };
        }

        // Amounts are signed: deposit refunds arrive negative so the total nets out.
        // Count, average, largest and last payment describe money coming in, so refund
        // rows are excluded from all of them (rows arrive newest first).
        let totalPayments = 0;
        let nonRefundTotal = 0;
        let paymentCount = 0;
        let largestPayment = 0;
        let lastPayment: Date | null = null;
        for (const t of allTransactions) {
            totalPayments += t.amount;
            if (t.transaction_type === 'deposit_refund') continue;
            nonRefundTotal += t.amount;
            paymentCount += 1;
            if (t.amount > largestPayment) largestPayment = t.amount;
            if (!lastPayment && t.transaction_at) lastPayment = new Date(t.transaction_at);
        }
        // The average describes a typical payment, so it divides the gross payments
        // received (refunds excluded) — not the net total shown in the tile.
        const averagePayment = paymentCount > 0 ? nonRefundTotal / paymentCount : 0;

        return {
            totalPayments,
            paymentCount,
            averagePayment,
            lastPayment,
            largestPayment,
        };
    }, [allTransactions]);

    const transactions = transactionsData?.transactions || [];
    const totalCount = transactionsData?.total || 0;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    return (
        <div className="space-y-6">
            {/* Payment Summary Cards */}
            <div className="grid gap-3 grid-cols-2 md:grid-cols-5">
                <StatCard
                    title="Total Payments"
                    value={
                        <>
                            {/* CurrencyDisplay renders the magnitude — sign a negative net explicitly */}
                            {stats.totalPayments < 0 && '−'}
                            <CurrencyDisplay amount={stats.totalPayments} />
                        </>
                    }
                    description="The cumulative sum of all payment transactions recorded for this contract, including deposits, advance repayments, and regular instalments, less any deposit refunded to you."
                />
                <StatCard
                    title="Payment Count"
                    value={stats.paymentCount}
                    description="The total number of individual payment transactions made towards this contract over its lifetime. Deposit refunds are not counted as payments."
                />
                <StatCard
                    title="Average Payment"
                    value={<CurrencyDisplay amount={stats.averagePayment} />}
                    description={`The mean payment amount calculated by dividing the payments you have made — deposit refunds excluded — by the number of those payments (${stats.paymentCount}).`}
                />
                <StatCard
                    title="Last Payment"
                    value={stats.lastPayment ? format(stats.lastPayment, 'MMM d, yyyy') : 'N/A'}
                    description="The date and time of the most recent payment transaction recorded for this contract."
                />
                <StatCard
                    title="Largest Payment"
                    value={<CurrencyDisplay amount={stats.largestPayment} />}
                    description="The highest single payment amount received in one transaction for this contract."
                />
            </div>

            {/* Transaction List */}
            <div className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search by description..."
                            className="pl-9"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {totalPages > 0 && (
                        <ListPagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            totalItems={totalCount}
                        />
                    )}
                </div>

                {isLoading ? (
                    <Skeleton className="h-48 w-full" />
                ) : transactions.length > 0 ? (
                    <>
                        {/* Mobile: Card layout */}
                        <div className="lg:hidden space-y-3">
                            {transactions.map((transaction) => {
                                const date = new Date(transaction.transaction_at);
                                return (
                                    <div
                                        key={transaction.id}
                                        className="p-4 rounded-xl border border-border/40 bg-card flex flex-col gap-2 shadow-sm"
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex gap-3 min-w-0 flex-1">
                                                <div className="bg-muted/30 rounded-full h-10 w-10 flex items-center justify-center font-medium text-xs shrink-0">
                                                    {format(date, 'MMM')}
                                                    <br />
                                                    {format(date, 'd')}
                                                </div>
                                                <div className="flex flex-col min-w-0">
                                                    <span className="font-medium text-sm break-all line-clamp-2">
                                                        {transaction.description || 'Payment'}
                                                    </span>
                                                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                        {format(date, 'h:mm a')}
                                                        {transaction.note && (
                                                            <>
                                                                <span>•</span>
                                                                <span className="truncate max-w-[120px]">{transaction.note}</span>
                                                            </>
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex flex-col items-end gap-1 shrink-0">
                                                <span className={cn(
                                                    "font-semibold",
                                                    transaction.amount >= 0 ? "text-green-600" : "text-red-600"
                                                )}>
                                                    {/* CurrencyDisplay renders the magnitude — sign the refunds explicitly */}
                                                    {transaction.amount < 0 && '−'}
                                                    <CurrencyDisplay amount={transaction.amount} />
                                                </span>
                                                {getTypeBadge(transaction.transaction_type)}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Desktop: Table layout */}
                        <div className="hidden lg:block">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Amount</TableHead>
                                        <TableHead>Description</TableHead>
                                        <TableHead>Notes</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {transactions.map((transaction) => {
                                        const date = new Date(transaction.transaction_at);
                                        return (
                                            <TableRow key={transaction.id}>
                                                <TableCell className="whitespace-nowrap">
                                                    <div>
                                                        <div className="font-medium">{format(date, 'MMM d, yyyy')}</div>
                                                        <div className="text-xs text-muted-foreground">{format(date, 'h:mm a')}</div>
                                                    </div>
                                                </TableCell>
                                                <TableCell className={cn(
                                                    "font-medium",
                                                    transaction.amount >= 0 ? "text-green-600" : "text-red-600"
                                                )}>
                                                    {transaction.amount < 0 && '−'}
                                                    <CurrencyDisplay amount={transaction.amount} />
                                                </TableCell>
                                                <TableCell className="max-w-[250px]">
                                                    <div className="flex items-center gap-2">
                                                        <span className="truncate">{transaction.description || '-'}</span>
                                                        {getTypeBadge(transaction.transaction_type)}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="max-w-[200px] truncate text-muted-foreground">
                                                    {transaction.note || '-'}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </>
                ) : (
                    <p className="text-center text-muted-foreground py-8">
                        {searchTerm ? 'No transactions match your search' : 'No transactions found'}
                    </p>
                )}
            </div>
        </div>
    );
}
